import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { padValue } from '@celerofinancas/core-ts';
import { TrackingService } from '@celerofinancas/service-utils';
import { getBankLogoUrl } from '@shared/utils/open-finance-bank-map';
import { OpenFinanceDirectoryService } from '@shared/services/open-finance-directory.service';
import { CompanyState } from '@celerofinancas/state-management';
import { NoContentModule } from '@celerofinancas/ui-common';
import { InfiniteScrollDirective, TooltipDirective } from '@celerofinancas/ui-directives';
import { ModalService, ModalRef } from '@celerofinancas/ui-modals';
import { FormatCashPipe } from '@celerofinancas/ui-pipes';
import { DataTableComponent } from '@shared/components/data-table/data-table.component';
import {
  DataTableAction,
  DataTableColumn,
  DataTableConfig,
  DataTableRow,
  DataTableSort,
} from '@shared/components/data-table/data-table.interface';
import { MainTitleComponent } from '@shared/components/main-title/main-title.component';
import { NavigationBarComponent } from '@shared/components/navigation-bar/navigation-bar.component';
import { ShimmerComponent } from '@shared/components/shimmer/shimmer.component';
import {
  TabsNavigationComponent,
  TabsNavigationConfig,
} from '@shared/components/tabs-navigation/tabs-navigation.component';
import { BootstrapCoordinator } from '@shared/models/bootstrap-coordinator';
import { finalize, NEVER, take, takeUntil, timeout } from 'rxjs';
import { cashIndicatorClass, signedCash, withCashIndicator } from '@shared/utils/cash-indicator';
import {
  BalanceByInstitutionItem,
  BalanceByTypeItem,
  InvestmentDetailsData,
  InvestmentDetailsResponse,
  InvestmentItem,
  InvestmentsListResponse,
  InvestmentsSummaryResponse,
  PaginationMeta,
} from './investments.interfaces';
import { InvestmentsService } from './investments.service';
import { InvestmentDetailsModalContentComponent } from './modal-content/modal-content.component';
import { environment } from '@environments/environment';
import { CAIXA_TRACKING_MODELS } from '@shared/models/tracking-models.enum';

/**
 * Investments component
 */
@Component({
  selector: 'caixa-webcomponent-investments',
  standalone: true,
  imports: [
    CommonModule,
    MainTitleComponent,
    NavigationBarComponent,
    TooltipDirective,
    InfiniteScrollDirective,
    DataTableComponent,
    TabsNavigationComponent,
    FormatCashPipe,
    ShimmerComponent,
    NoContentModule,
  ],
  templateUrl: './investments.component.html',
  styleUrls: ['./investments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InvestmentsComponent extends BootstrapCoordinator implements OnInit {
  /**
   * API page size
   */
  private static readonly PAGE_SIZE = 20;

  /**
   * REPRO 86e1r18xt — timeout (ms) da chamada de detalhes. Garante que o estado
   * loadingDetails sempre seja liberado, mesmo se o backend pendurar.
   */
  private static readonly DETAILS_TIMEOUT_MS = 15000;

  /**
   * Signs an amount by its natural direction, so the cash pipes render the
   * matching C/D indicator. Exposed for the template (card 86e37v532).
   */
  public readonly signedCash = signedCash;

  /**
   * Resolves the color class from the C/D indicator an amount renders.
   * Exposed for the template (card 86e37v532).
   */
  public readonly cashIndicatorClass = cashIndicatorClass;

  /**
   * Currently active tab identifier
   */
  public activeTab = 'renda-fixa';

  /**
   * FIX 86e1r18xt — cache do tableData (memoização): evita recriar o array a cada change
   * detection, o que forçaria o cdk-table a re-renderizar as linhas/botões.
   */
  private tableDataCacheSource: InvestmentItem[] | null = null;
  private tableDataCache: DataTableRow[] = [];

  /**
   * Modal service for handling modals
   */
  private readonly modalService = inject(ModalService);

  /**
   * Open Finance directory service for dynamic bank logos
   */
  private readonly openFinanceDirectory = inject(OpenFinanceDirectoryService);

  /**
   * Investments service
   */
  private readonly investmentsService = inject(InvestmentsService);

  /**
   * Change detector
   */
  private readonly cdr = inject(ChangeDetectorRef);

  /**
   * Tracking service
   */
  private readonly tracking = inject(TrackingService);

  /**
   * Loading state for investments
   */
  public isLoadingInvestments = signal(false);

  /**
   * Loading state for summary
   */
  public isLoadingSummary = signal(false);

  /**
   * Loading state for details modal
   */
  public loadingDetails = signal(false);

  /**
   * Loading state while fetching next page
   */
  public isLoadingMoreInvestments = signal(false);

  /**
   * Pagination state for investments list
   */
  public pagination = signal<PaginationMeta>({
    page: 1,
    pageSize: InvestmentsComponent.PAGE_SIZE,
    totalItems: 0,
    totalPages: 1,
  });

  /**
   * Last update timestamp
   */
  public lastUpdate = new Date();

  /**
   * Sort key mapping for API
   */
  private readonly SORT_KEY_MAP: Record<string, string> = {
    productName: 'productName',
    grossAmount: 'grossAmount',
    netAmount: 'netAmount',
    quantity: 'quantity',
    unitValue: 'unitValue',
    maturityDate: 'maturityDate',
  };

  /**
   * Current sort state
   */
  public currentSort: { sortBy: string; sortOrder: 'asc' | 'desc' } | null = null;

  /**
   * Shared logo config reused across institution column
   */
  private readonly logoConfig = {
    imageUrlKey: 'institutionLogoUrl',
    colorMap: {
      Itaú: '#F59E0B',
      BTG: '#1E40AF',
      Santander: '#DC2626',
      XP: '#EF4444',
    },
    textMap: {
      Itaú: 'itaú',
      BTG: 'btg',
      Santander: 'S',
      XP: 'XP',
    },
    defaultColor: 'var(--cds-text-light)',
  };

  /**
   * Table configuration
   */
  public tableConfig: DataTableConfig = {
    columns: [],
    sortable: true,
    emptyMessage: 'Nenhum investimento encontrado',
    showActions: true,
    actionsColumnWidth: '50px',
    title: 'Lista de Investimentos',
  };

  /**
   * Table actions configuration
   */
  public tableActions: DataTableAction[] = [
    {
      icon: 'cds-icon-search',
      label: '',
      action: (row: Record<string, unknown>) => this.openInvestmentDetails(row),
      variant: 'solid',
      size: 'lg',
      showLabel: false,
    },
  ];

  /**
   * Component data including cards and investments
   */
  public data: {
    cards: {
      totalBalance: number;
      feesTaxes: number;
      balanceByType: BalanceByTypeItem[];
      balanceByInstitution: BalanceByInstitutionItem[];
    };
    investments: InvestmentItem[];
  } = {
    cards: {
      totalBalance: 0,
      feesTaxes: 0,
      balanceByType: [] as BalanceByTypeItem[],
      balanceByInstitution: [] as BalanceByInstitutionItem[],
    },
    investments: [],
  };

  /**
   * Tabs navigation configuration
   */
  public tabsConfig: TabsNavigationConfig = {
    tabs: [
      { id: 'renda-fixa', label: 'Renda Fixa' },
      { id: 'renda-variavel', label: 'Renda Variável' },
      { id: 'fundos', label: 'Fundo de Investimentos' },
    ],
  };

  /**
   * Table columns configuration (dynamic)
   */
  public tableColumns: DataTableColumn[] = [];

  /**
   * Actions for currently active tab
   */
  public get currentTableActions(): DataTableAction[] {
    return this.activeTab === 'renda-variavel' ? [] : this.tableActions;
  }

  /**
   * Get table data for DataTable component
   */
  public get tableData(): DataTableRow[] {
    // Recalcula apenas quando a referência de data.investments muda (memoização).
    if (this.tableDataCacheSource !== this.data.investments) {
      this.tableDataCacheSource = this.data.investments;
      this.tableDataCache = this.data.investments.map((investment) => ({
        ...investment,
        institutionLogoUrl:
          getBankLogoUrl(investment.companyCnpj, environment.staticUrl) ??
          this.openFinanceDirectory.getLogoUrl(investment.companyCnpj) ??
          undefined,
      })) as unknown as DataTableRow[];
    }
    return this.tableDataCache;
  }

  /**
   * Whether there's a next page to load
   */
  public get hasNextPage(): boolean {
    const pagination = this.pagination();
    return pagination.page < pagination.totalPages;
  }

  /**
   * Whether end-of-list indicator should be visible
   */
  public get showListEndIndicator(): boolean {
    return (
      !this.isLoadingInvestments() &&
      !this.isLoadingMoreInvestments() &&
      this.data.investments.length > 0 &&
      !this.hasNextPage
    );
  }

  /**
   * Client identity number (CNPJ) derived from state
   */
  public get clientIdentityNumber(): string {
    return padValue(
      this.store.selectSnapshot(CompanyState.getActiveCompany)?.identity?.number || 0,
      14,
    );
  }

  /**
   * Initializing the component to update the displayedColumns array based on the current table configuration and available actions.
   */
  public override ngOnInit(): void {
    super.ngOnInit();
    this.tracking.track(CAIXA_TRACKING_MODELS.investmentsView);
    this.updateTableColumns();
    this.store
      .select(CompanyState.getActiveCompany)
      .pipe(takeUntil(this.destroyController$))
      .subscribe(() => {
        this.loadSummary();
        this.loadInvestmentsForActiveTab();
      });
    this.openFinanceDirectory.load();
    this.openFinanceDirectory.loaded$
      .pipe(takeUntil(this.destroyController$))
      .subscribe(() => this.cdr.markForCheck());
  }

  /**
   * Helpers to build columns (reuse across tabs)
   *
   * @param partial - Partial column configuration to merge
   * @returns Complete DataTableColumn configuration
   */
  private createColumn(partial: DataTableColumn): DataTableColumn {
    return {
      sortable: true,
      width: '150px',
      align: 'left',
      ...partial,
    } as DataTableColumn;
  }

  /**
   * Create a currency column
   *
   * @param key - Column key
   * @param label - Column label
   * @param width - Column width (default: '50px')
   * @param tooltip - Optional tooltip text
   * @returns DataTableColumn configured for currency display
   */
  private createCurrencyColumn(
    key: string,
    label: string,
    width = '50px',
    tooltip?: string,
  ): DataTableColumn {
    return this.createColumn({
      key,
      label,
      width,
      align: 'right',
      tooltip,
      renderType: 'currency',
      renderConfig: { currency: 'BRL', showSymbol: true },
    });
  }

  /**
   * Create a status column
   *
   * @param key - Column key
   * @param label - Column label
   * @param width - Column width (default: '200px')
   * @returns DataTableColumn configured for status display
   */
  private createStatusColumn(key: string, label: string, width = '200px'): DataTableColumn {
    return this.createColumn({
      key,
      label,
      width,
      align: 'left',
      renderType: 'status',
      renderConfig: {
        statusMap: {
          true: { text: 'SIM', class: 'status-default' },
          false: { text: 'NÃO', class: 'status-default' },
        },
      },
    });
  }

  /**
   * Create an institution column
   */
  private createInstitutionColumn(): DataTableColumn {
    return {
      key: 'brandName',
      label: 'Instituição',
      sortable: false,
      width: '50px',
      align: 'center',
      renderType: 'logo',
      renderConfig: { logoConfig: this.logoConfig },
    };
  }

  /**
   * Create a name column
   */
  private createNameColumn(): DataTableColumn {
    return this.createColumn({
      key: 'productName',
      label: 'Nome do Investimento',
      width: '300px',
      align: 'left',
    });
  }

  /**
   * Create a gross column
   */
  private createGrossColumn(): DataTableColumn {
    return this.createCurrencyColumn(
      'grossAmount',
      'Saldo Bruto',
      '50px',
      'Valor total investido até o dia anterior (D-1), sem descontos.',
    );
  }

  /**
   * Create a net column
   */
  private createNetColumn(): DataTableColumn {
    return this.createCurrencyColumn(
      'netAmount',
      'Saldo Líquido',
      '50px',
      'Valor total investido até o dia anterior (D-1), após descontos de taxas e impostos (quando aplicável).',
    );
  }

  /**
   * Create an available column
   */
  private createAvailableColumn(): DataTableColumn {
    return this.createStatusColumn('availableForRedemption', 'Disponível para resgate', '180px');
  }

  /**
   * Create a quantity column
   */
  private createQuantityColumn(): DataTableColumn {
    return this.createColumn({
      key: 'quantity',
      label: 'Qtd. de Ações',
      width: '100px',
      align: 'left',
    });
  }

  /**
   * Create a unit value column
   */
  private createUnitValueColumn(): DataTableColumn {
    return this.createCurrencyColumn('unitValue', 'Preço de fechamento da ação', '220px');
  }

  /**
   * Resolve columns for current tab
   *
   * @param tabId - Tab ID to get columns for
   * @returns Array of DataTableColumn configurations
   */
  private getColumnsForTab(tabId: string): DataTableColumn[] {
    switch (tabId) {
      case 'renda-variavel':
        return [
          this.createInstitutionColumn(),
          this.createNameColumn(),
          this.createQuantityColumn(),
          this.createUnitValueColumn(),
          this.createGrossColumn(),
        ];
      case 'fundos':
        return [
          this.createInstitutionColumn(),
          this.createNameColumn(),
          this.createGrossColumn(),
          this.createNetColumn(),
        ];
      case 'renda-fixa':
      default:
        return [
          this.createInstitutionColumn(),
          this.createNameColumn(),
          this.createGrossColumn(),
          this.createNetColumn(),
          this.createAvailableColumn(),
        ];
    }
  }

  /**
   * Update table columns for active tab with fresh objects
   */
  private updateTableColumns(): void {
    const cols = this.getColumnsForTab(this.activeTab).map((col) => ({ ...col }));
    this.tableColumns = cols;
    this.tableConfig = {
      ...this.tableConfig,
      columns: cols,
      showActions: this.activeTab !== 'renda-variavel',
    };
    this.cdr.markForCheck();
  }

  /**
   * Load summary of investments
   */
  private loadSummary(): void {
    this.isLoadingSummary.set(true);

    this.investmentsService
      .getSummary(this.clientIdentityNumber)
      .pipe(take(1))
      .subscribe({
        next: (res: InvestmentsSummaryResponse) => {
          this.lastUpdate = new Date(res.lastUpdated);

          const { investmentsByType } = res.summary;
          const balanceByType = [
            {
              type: 'Renda Fixa',
              balance: investmentsByType?.RENDA_FIXA?.grossAmount ?? 0,
              color: '#8B5CF6',
              tooltip:
                '<b>Bancária:</b> Investimentos oferecidos por bancos, como CDB e LCI, com retorno e prazo definidos desde o início.<br><b>Crédito:</b> Aplicações em títulos de empresas, como Debêntures e CRI, com rendimento previsível e risco um pouco maior do que produtos bancários.',
            },
            {
              type: 'Renda Variável',
              balance: investmentsByType?.RENDA_VARIAVEL?.grossAmount ?? 0,
              color: '#10B981',
              tooltip:
                'Investimentos como ações, que não garantem retorno fixo e podem variar de acordo com o mercado.',
            },
            {
              type: 'Fundos de Investimentos',
              balance: investmentsByType?.FUNDOS?.grossAmount ?? 0,
              color: '#F59E0B',
              tooltip:
                'Modalidade em que o dinheiro é reunido com o de outros investidores e aplicado por um gestor profissional, conforme a estratégia do fundo.',
            },
          ];

          const balanceByInstitution = res.summary.investmentsByInstitution.map((inst, index) => ({
            institution: inst.institutionName,
            balance: inst.grossAmount,
            color: this.getInstitutionColorByIndex(index),
          }));

          this.data = {
            ...this.data,
            cards: {
              totalBalance: res.summary.totalGrossAmount ?? 0,
              feesTaxes: res.summary.totalTaxes ?? 0,
              balanceByType,
              balanceByInstitution,
            },
          };

          this.isLoadingSummary.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoadingSummary.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load investments for the currently active tab
   */
  private loadInvestmentsForActiveTab(): void {
    this.updateTableColumns();
    this.pagination.set({
      page: 1,
      pageSize: InvestmentsComponent.PAGE_SIZE,
      totalItems: 0,
      totalPages: 1,
    });

    this.data = { ...this.data, investments: [] };
    this.cdr.markForCheck();

    this.isLoadingInvestments.set(true);
    this.loadInvestmentsPage(1, false);
  }

  /**
   * Load next page for active tab when user reaches scroll threshold
   */
  public onInfiniteScroll(): void {
    if (this.isLoadingInvestments() || this.isLoadingMoreInvestments() || !this.hasNextPage) {
      return;
    }

    this.isLoadingMoreInvestments.set(true);
    this.loadInvestmentsPage(this.pagination().page + 1, true);
  }

  /**
   * Executes the API call for current tab and requested page
   *
   * @param page - Page number to load
   * @param append - Whether to append results to existing list
   */
  private loadInvestmentsPage(page: number, append: boolean): void {
    const sortBy = this.currentSort ? this.SORT_KEY_MAP[this.currentSort.sortBy] : undefined;
    const sortOrder = this.currentSort?.sortOrder;

    let apiCall;

    switch (this.activeTab) {
      case 'renda-fixa':
        apiCall = this.investmentsService.getFixedIncomeInvestments(
          this.clientIdentityNumber,
          page,
          InvestmentsComponent.PAGE_SIZE,
          sortBy,
          sortOrder,
        );
        break;
      case 'renda-variavel':
        apiCall = this.investmentsService.getVariableIncomeInvestments(
          this.clientIdentityNumber,
          page,
          InvestmentsComponent.PAGE_SIZE,
          sortBy,
          sortOrder,
        );
        break;
      case 'fundos':
        apiCall = this.investmentsService.getInvestmentFunds(
          this.clientIdentityNumber,
          page,
          InvestmentsComponent.PAGE_SIZE,
          sortBy,
          sortOrder,
        );
        break;
      default:
        apiCall = this.investmentsService.getFixedIncomeInvestments(
          this.clientIdentityNumber,
          page,
          InvestmentsComponent.PAGE_SIZE,
          sortBy,
          sortOrder,
        );
    }

    apiCall.pipe(take(1)).subscribe({
      next: (res: InvestmentsListResponse) => {
        const investments = res.investments || [];
        const currentInvestments = append ? this.data.investments : [];
        const pagination = res.pagination ?? {
          page,
          pageSize: InvestmentsComponent.PAGE_SIZE,
          totalItems: currentInvestments.length + investments.length,
          totalPages: 1,
        };

        this.data = {
          ...this.data,
          investments: [...currentInvestments, ...investments],
        };
        this.pagination.set(pagination);

        this.isLoadingInvestments.set(false);
        this.isLoadingMoreInvestments.set(false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.isLoadingInvestments.set(false);
        this.isLoadingMoreInvestments.set(false);
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Get institution color by index
   *
   * @param index - Index to get color for
   * @returns Color string for the institution
   */
  private getInstitutionColorByIndex(index: number): string {
    const colors = ['#F59E0B', '#1E40AF', '#DC2626', '#EF4444', '#10B981'];
    return colors[index % colors.length];
  }

  /**
   * Get institution color for display
   *
   * @param institution - Institution name to get color for
   * @returns Color string for the institution
   */
  public getInstitutionColor(institution: string): string {
    const item = this.data.cards.balanceByInstitution.find((i) => i.institution === institution);
    return item?.color || 'var(--cds-text-light)';
  }

  /**
   * Set active tab
   *
   * @param tab - Tab ID to set as active
   * @returns void
   */
  public setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  /**
   * Handle tab change event
   *
   * @param tabId - ID of the tab to switch to
   * @returns void
   */
  public onTabChange(tabId: string): void {
    this.tracking.track(CAIXA_TRACKING_MODELS.investmentsTabChange, { tab: tabId });
    this.activeTab = tabId;
    this.updateTableColumns();
    this.loadInvestmentsForActiveTab();
  }

  /**
   * Open investment details modal
   * Displays detailed information about the selected investment
   *
   * @param investment - Investment data to show details for
   * @returns void
   */
  public openInvestmentDetails(investment: Record<string, unknown>): void {
    const investmentId = investment.investmentId as string;

    if (!investmentId || this.loadingDetails()) {
      return;
    }

    const investmentType = this.mapInvestmentCategoryToType(
      investment.investmentCategory as string,
    );

    this.tracking.track(CAIXA_TRACKING_MODELS.investmentsOpenDetails, {
      tab: this.activeTab,
      investmentId,
      investmentType,
    });
    const modalLoading = signal(true);
    const modalHasError = signal(false);
    const modalInvestmentData = signal<InvestmentDetailsData | null>(null);

    let modalRef: ModalRef | undefined;
    try {
      modalRef = this.modalService.open(InvestmentDetailsModalContentComponent, {
        headerTitle: this.getModalTitle(investmentType),
        investmentData: modalInvestmentData,
        investmentType: investmentType,
        loading: modalLoading,
        hasError: modalHasError,
        size: 'lg',
      });
    } catch {
      return;
    }

    this.loadingDetails.set(true);
    this.investmentsService
      .getInvestmentDetails(investmentId, this.clientIdentityNumber)
      .pipe(
        take(1),
        // FIX 86e1r18xt (principal): cancela a chamada e libera loadingDetails ao FECHAR
        // o modal. Sem isso, fechar com a chamada de detalhes ainda pendente deixa
        // loadingDetails preso em true e TODO clique seguinte morre no guard (modal
        // nunca mais abre) — exatamente o bug reproduzido em prod.
        takeUntil(modalRef?.afterClosed$ ?? NEVER),
        // FIX 86e1r18xt (rede de segurança): garante o finalize mesmo se o backend
        // pendura e o usuário NÃO fecha o modal.
        timeout(InvestmentsComponent.DETAILS_TIMEOUT_MS),
        finalize(() => {
          modalLoading.set(false);
          this.loadingDetails.set(false);
        }),
      )
      .subscribe({
        next: (details: InvestmentDetailsResponse) => {
          if (!details?.investmentId) {
            modalHasError.set(true);
            return;
          }

          const investmentData: InvestmentDetailsData = this.mapApiResponseToModalData(details);
          modalInvestmentData.set(investmentData);
        },
        error: () => {
          modalHasError.set(true);
        },
      });
  }

  /**
   * Get modal title based on investment type
   *
   * @param investmentType - Investment type to get title for
   * @returns Modal title string
   */
  private getModalTitle(investmentType: string): string {
    const titles: Record<string, string> = {
      'renda-fixa': 'Detalhe da Renda Fixa',
      fundos: 'Detalhe do Fundo de Investimento',
      'renda-variavel': 'Detalhe da Renda Variável',
    };
    return titles[investmentType] || 'Detalhe do Investimento';
  }

  /**
   * Map API response to modal data
   *
   * @param details - API response data to map
   * @returns Mapped modal data
   */
  private mapApiResponseToModalData(details: InvestmentDetailsResponse): InvestmentDetailsData {
    return {
      institution: details.institutionName,
      name: details.productName,
      grossBalance: withCashIndicator(
        this.formatCurrency(details.grossAmount),
        details.grossAmount,
        'credit',
      ),
      netBalance: withCashIndicator(this.formatCurrency(details.netAmount), details.netAmount, 'credit'),
      provisionedFeesTaxes: withCashIndicator(
        this.formatCurrency(details.totalTaxes),
        details.totalTaxes,
        'debit',
      ),
      startDate: new Date(details.purchaseDate).toLocaleDateString('pt-BR'),
      maturityDate: new Date(details.maturityDate).toLocaleDateString('pt-BR'),
      minimumRedemptionDate: new Date(details.maturityDate).toLocaleDateString('pt-BR'),
      yieldPeriodicity: this.mapPeriodicity(details.incomePeriodicity),
      numberOfTitles: details.quantity?.toString(),
      indexer: details.indexer,
      agreedRate: details.agreedRate?.toString(),
      cnpj: this.formatCnpj(details.institutionCnpj),
      category: this.mapInvestmentCategory(details.investmentCategory),
      numberOfShares: details.quantity?.toString(),
      shareValue: withCashIndicator(this.formatCurrency(details.unitValue), details.unitValue, 'credit'),
    };
  }

  /**
   * Map investment category to type
   *
   * @param category - Investment category to map
   * @returns Investment type string
   */
  private mapInvestmentCategoryToType(category: string): string {
    const categoryMap: Record<string, string> = {
      RENDA_FIXA: 'renda-fixa',
      FUNDOS: 'fundos',
      RENDA_VARIAVEL: 'renda-variavel',
    };
    return categoryMap[category] || 'renda-fixa';
  }

  /**
   * Map investment category to display name
   *
   * @param category - Investment category to map
   * @returns Display name for the category
   */
  private mapInvestmentCategory(category: string): string {
    const categoryMap: Record<string, string> = {
      RENDA_FIXA: 'Renda Fixa',
      FUNDOS: 'Fundos de Investimento',
      RENDA_VARIAVEL: 'Renda Variável',
    };
    return categoryMap[category] || 'Renda Fixa';
  }

  /**
   * Map periodicity to display name
   *
   * @param periodicity - Periodicity to map
   * @returns Display name for the periodicity
   */
  private mapPeriodicity(periodicity: string): string {
    const periodicityMap: Record<string, string> = {
      MENSAL: 'Mensal',
      DIARIO: 'Diário',
      TRIMESTRAL: 'Trimestral',
      SEMESTRAL: 'Semestral',
      ANUAL: 'Anual',
    };
    return periodicityMap[periodicity] || 'Mensal';
  }

  /**
   * Format CNPJ
   *
   * @param cnpj - CNPJ string to format
   * @returns Formatted CNPJ string
   */
  private formatCnpj(cnpj: string): string {
    if (!cnpj) return '';
    return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  /**
   * Format currency value to Brazilian Real with 2 decimal places
   *
   * @param value - Numeric value to format
   * @returns Formatted currency string
   */
  private formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  /**
   * Handle table sort change event
   *
   * @param sort - Sort configuration
   * @returns void
   */
  public onSortChange(sort: DataTableSort): void {
    this.currentSort = {
      sortBy: sort.column,
      sortOrder: sort.direction as 'asc' | 'desc',
    };

    this.tracking.track(CAIXA_TRACKING_MODELS.investmentsSort, {
      tab: this.activeTab,
      sortBy: this.currentSort.sortBy,
      sortOrder: this.currentSort.sortOrder,
    });

    this.loadInvestmentsForActiveTab();
  }

  /**
   * Abre o modal de detalhes ao clicar em QUALQUER parte da linha (não só na lupa).
   * Respeita a regra de negócio: abas sem ação (ex.: renda variável, onde
   * `currentTableActions` é vazio) NÃO abrem o modal.
   *
   * @param row - linha clicada
   * @returns void
   */
  public onRowClicked(row: DataTableRow): void {
    const clickable = this.currentTableActions.length > 0;

    if (!clickable) {
      return;
    }

    this.openInvestmentDetails(row as Record<string, unknown>);
  }
}
